import {
  blackColor,
  cardTitle,
  dangerColor,
  grayColor,
  infoColor,
  successColor,
  whiteColor,
  warningColor,
  blendColors,
  hexToRgb,
} from "../../../assets/jss/material-dashboard-pro-react";
import modalStyle from "../../../assets/jss/material-dashboard-pro-react/modalStyle";
import customDropdownStyle from "../../../assets/jss/material-dashboard-pro-react/components/customDropdownStyle";

export const inputsStyles = (theme) => {
  // ``blendColors(c1,c2,a)`` = ``a*c1 + (1-a)*c2``.
  // Base entre verde saturado e pastel; listras puxam um pouco mais para
  // cada lado — fundo mais escuro que só ``[5]/[6]``, zebrado ainda bem visível.
  const processingStripeDark = successColor[0];
  const processingStripeLight = successColor[0] + "bb";
  return {
    ...modalStyle(theme),
    ...customDropdownStyle(theme),
    inputsCardWrapper: {
      width: "100%",
      overflow: "hidden",
      "@media (max-width: 1320px)": {
        "&:not(.loading)": {
          overflow: "auto !important",
        },
      },
    },
    cardIconTitle: {
      ...cardTitle,
      marginTop: "15px",
      marginBottom: "0px",
    },
    chipWaiting: {
      background:
        "linear-gradient(60deg, " + grayColor[5] + ", " + grayColor[4] + ")",
    },
    chipInProgress: {
      background:
        "linear-gradient(60deg, " +
        successColor[5] +
        ", " +
        successColor[6] +
        ")",
    },
    // ``processing`` (V3): efeito "onda" horizontal em 2 tons de verde.
    // Padrao copiado do ``loading`` pip do StagePips (que e' o shimmer
    // cinza dos inputs em carregamento) — gradiente linear 90deg liso
    // (dark → light → dark) com 200% de largura, deslizando lateralmente
    // via ``backgroundPosition``. ``ease-in-out`` da a sensacao de onda
    // "respirando" em vez de listras correndo. Listras quebravam em
    // chips pequenos; o shimmer fica suave em qualquer tamanho.
    chipProcessing: {
      color: whiteColor,
      background: `linear-gradient(90deg, ${processingStripeDark} 0%, ${processingStripeLight} 50%, ${processingStripeDark} 100%)`,
      backgroundSize: "200% 100%",
      animation: "$chipProcessingWave 1400ms ease-in-out infinite",
      "& .MuiChip-icon": {
        color: whiteColor,
      },
    },
    chipSuccess: {
      color: whiteColor,
      background:
        "linear-gradient(60deg, " +
        successColor[1] +
        ", " +
        successColor[2] +
        ")",
    },
    chipWarning: {
      color: whiteColor,
      background:
        "linear-gradient(60deg, " +
        warningColor[1] +
        ", " +
        warningColor[2] +
        ")",
    },
    chipError: {
      color: whiteColor,
      background:
        "linear-gradient(60deg, " +
        dangerColor[1] +
        ", " +
        dangerColor[2] +
        ")",
    },
    // ``chipSystemError`` (V3, D-35 leva 96): falha tecnica genuina
    // do pipeline (input/bloco/xlink em ``system_error``). Roxo escuro
    // diferencia visualmente de ``chipError`` (vermelho — acao do
    // cliente). Mesma cor da entrada ``system_error`` em
    // ``MONTH_STATUS_LEGEND`` (``services/inputsV3StateRules.js``)
    // e da chave ``COLORS.system_error`` em ``StagePips.js``.
    chipSystemError: {
      // Preto (em vez do roxo da leva 96): "erro de processamento" e'
      // o estado mais grave/raro do sistema (falha tecnica do
      // pipeline) — preto comunica "interrompido / sem alternativa"
      // melhor que roxo, e fica inconfundivel ao lado dos outros
      // chips coloridos. Gradiente sutil (cinza 800 -> preto puro)
      // mantem consistencia visual com os demais chips do conjunto.
      color: whiteColor,
      background: "linear-gradient(60deg, #424242, #000000)",
    },
    // ``stale`` (V3): calculo completado, mas algo mudou depois.
    // Gradiente verde → amarelo (SuccessColor[2] → warningColor[1])
    // comunica "estava OK, virou pendente" sem soar como erro.
    chipStale: {
      color: whiteColor,
      background:
        "linear-gradient(60deg, " +
        successColor[2] +
        ", " +
        warningColor[1] +
        ")",
    },
    // Onda 3.4 (CCU Testes, mai/2026): "Pronto para calculo" usa
    // verde solido (analogo ao ``chipSuccess``). Antes, este estado
    // reusava ``chipStale`` (gradient verde->amarelo), que o consultor
    // leu como "atencao" — visualmente nao casava com a semantica de
    // "todos os inputs OK, pronto para rodar".
    chipReadyToCalculate: {
      color: whiteColor,
      background:
        "linear-gradient(60deg, " +
        successColor[1] +
        ", " +
        successColor[2] +
        ")",
    },
    chipIcon: {
      color: whiteColor,
    },
    blocksSeparator: {
      marginTop: "10px",
      borderTop:
        "2px dotted " +
        blendColors(theme.palette.custom.clientColor, whiteColor, 0.5),
    },
    inputBox: {
      display: "inline-block",
      marginRight: "10px",
      backgroundColor: grayColor[19] + "bb",
      borderRadius: "6px",
      padding: "8px",
      width: "150px",
      height: "150px",
      position: "relative",
      cursor: "pointer",
      transition: "all 0.2s",
      "&:hover": {
        transform: "scale(1.06)",
        backgroundColor: grayColor[19] + "ff",
      },
      "&.error": {
        color: whiteColor,
        backgroundColor: "#dd0000",
      },
      "&.running": {
        backgroundColor: grayColor[10],
        backgroundImage:
          "linear-gradient(-45deg, rgba(255, 255, 255, 0.25) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.25) 50%, rgba(255, 255, 255, 0.25) 75%, transparent 75%, transparent)",
        backgroundSize: "15px 15px",
        animation: "$move 2s linear infinite",
      },
    },
    fileStatus: {
      position: "absolute",
      top: "87%",
      width: "26px",
      height: "26px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transform: "translate(-50%,-50%)",
      backgroundColor: grayColor[3],
      color: whiteColor,
      borderRadius: "6px",
      "&.success": {
        backgroundColor: successColor[3],
      },
      "&.warning": {
        backgroundColor: warningColor[3],
      },
      "&.error": {
        backgroundColor: dangerColor[3],
      },
      "&.running": {
        backgroundColor: grayColor[1],
        backgroundImage:
          "linear-gradient(-45deg, rgba(255, 255, 255, 0.25) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.25) 50%, rgba(255, 255, 255, 0.25) 75%, transparent 75%, transparent)",
        backgroundSize: "15px 15px",
        animation: "$move 2s linear infinite",
        color: whiteColor,
      },
    },
    "@keyframes move": {
      "0%": {
        backgroundPosition: "0 0",
      },
      "100%": {
        backgroundPosition: "15px 15px",
      },
    },
    // Shimmer/onda: gradiente de 200% de largura deslizando de uma
    // ponta a outra. Espelha ``pipsLoadingShimmer`` do StagePips.
    "@keyframes chipProcessingWave": {
      "0%": { backgroundPosition: "200% 0" },
      "100%": { backgroundPosition: "-200% 0" },
    },
    drawerHeader: {
      borderBottom: "1px solid " + theme.palette.custom.clientColor,
      padding: "4px 12px",
      backgroundColor: grayColor[15],
    },
    accordionRoot: {
      "& > div:first-child": {
        paddingTop: "24px",
      },
    },
    accordionTitle: {
      paddingLeft: "12px",
      color: grayColor[7],
      "&:hover,&:focus": {
        color: grayColor[6],
      },
    },
    expandIcon: {
      color: grayColor[7],
      "&:hover,&:focus": {
        color: grayColor[6],
      },
    },
    inputDrawerPaper: {
      borderRadius: "10px",
      margin: "1vh 1vh 1vh 0",
      height: "98vh",
      width: "720px",
      overflowX: "hidden",
    },
    inputDrawerTitleSuffix: {
      color: grayColor[3],
    },
    inputDrawerAccordionWrapper: {
      margin: "0",
    },
    inputDrawerStatusRoot: {
      width: "100%",
      display: "block",
    },
    inputDrawerMonthCompleteBanner: {
      textAlign: "center",
      backgroundColor: dangerColor[4] + "22",
      fontSize: "12px",
      borderRadius: "4px",
      margin: "8px 6px",
    },
    inputDrawerTableCellIcon: {
      width: "5%",
      paddingLeft: "0",
      paddingRight: "0",
    },
    inputDrawerTableCellStage: {
      padding: "6px 6px 6px 0",
      height: "1px",
    },
    inputDrawerTableCellFileActions: {
      paddingTop: "6px",
      paddingLeft: "6px",
      paddingRight: "12px",
    },
    inputDrawerTooltipHelpRow: {
      display: "flex",
      height: "100%",
      cursor: "help",
    },
    inputDrawerStageIcon: {
      marginRight: "6px",
    },
    inputDrawerSentByWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingBottom: "12px",
      marginBottom: "12px",
      borderBottom: "1px solid #eeeeee",
    },
    inputDrawerSentByBox: {
      width: "100%",
      textAlign: "center",
      fontSize: "11px",
      lineHeight: "1.6",
      backgroundColor: grayColor[15],
      borderRadius: "4px",
    },
    inputDrawerSendNewFileLabel: {
      textAlign: "center",
      textTransform: "uppercase",
      fontSize: "11px",
    },
    inputDrawerFileDropZone: {
      height: "90px",
      margin: "0 0 8px 0",
      border: "1px dashed #d1d1d1",
      borderRadius: "4px",
      position: "relative",
    },
    inputDrawerFileDropZoneDragging: {
      backgroundColor: grayColor[13],
    },
    inputDrawerFileDropZoneWrong: {
      backgroundColor: dangerColor[0] + "11",
    },
    inputDrawerFileDropZoneHasFile: {
      backgroundColor: successColor[0] + "11",
    },
    inputDrawerFileDropZoneEmpty: {
      backgroundColor: whiteColor,
    },
    inputDrawerHiddenFileInput: {
      display: "none",
    },
    inputDrawerFileNameOverlay: {
      position: "absolute",
      top: "46%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      fontSize: "16px",
      maxWidth: "65%",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    inputDrawerFileNameOverlayNoPointer: {
      pointerEvents: "none",
    },
    inputDrawerFileHintOverlay: {
      position: "absolute",
      top: "80%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      fontSize: "12px",
      fontStyle: "italic",
      color: grayColor[0],
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      paddingLeft: "5px",
      paddingRight: "5px",
    },
    inputDrawerFileHintOverlayNoPointer: {
      pointerEvents: "none",
    },
    inputDrawerFileLink: {
      textDecoration: "underline",
      cursor: "pointer",
    },
    inputDrawerFileLinkThemed: {
      color: theme.palette.custom.clientColor,
    },
    inputDrawerFileLinkNoPointer: {
      pointerEvents: "none",
    },
    inputDrawerWrongTypeBadge: {
      position: "absolute",
      top: "20%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      fontSize: "11px",
      color: whiteColor,
      backgroundColor: dangerColor[0],
      borderRadius: "4px",
      lineHeight: "1",
      padding: "2px 4px",
    },
    inputDrawerWrongTypeBadgeNoPointer: {
      pointerEvents: "none",
    },
    inputDrawerSendIconWrap: {
      position: "absolute",
      top: "50%",
      right: "25px",
      transform: "translate(0,-50%)",
      width: "40px",
      height: "40px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    inputDrawerSendIconWrapNoPointer: {
      pointerEvents: "none",
    },
    inputDrawerCloudUploadIcon: {
      width: "30px",
      height: "30px",
    },
    inputDrawerReplicateRow: {
      marginTop: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    inputDrawerOrSpacing: {
      marginRight: "6px",
    },
    inputDrawerReplicateButton: {
      margin: "0 4px",
    },
    inputDrawerUploadingLabel: {
      fontSize: "11px",
      textTransform: "uppercase",
    },
    inputDrawerDownloadActionsRow: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingTop: "12px",
      marginTop: "12px",
      borderTop: "1px solid #eeeeee",
    },
    inputDrawerDownloadButton: {
      margin: "0",
    },
    inputDrawerGetAppIcon: {
      width: "15px",
      height: "15px",
    },
    inputDrawerSmallIcon: {
      width: "15px",
      height: "15px",
    },
    inputDrawerReprocessButton: {
      margin: "0 0 0 4px",
    },
    inputDrawerAutoGenDownloadWrap: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginTop: "12px",
    },
    inputDrawerNotesCellWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    },
    inputDrawerNotesButton: {
      margin: "0 4px",
    },
    inputDrawerDownloadMenuList: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    inputDetailsRoot: {
      padding: "0 12px",
      width: "100%",
    },
    inputDetailsTagRow: {
      margin: "1px -2px 0 -2px",
    },
    inputDetailsConnectionsRow: {
      margin: "-3px -2px 0 -2px",
    },
    inputDetailsSaveRow: {
      margin: "-8px 0 8px",
    },
    inputDetailsEditorWrap: {
      width: "100%",
    },
    inputDetailsEditorWrapHidden: {
      display: "none",
    },
    inputDetailsEditorWrapVisible: {
      display: "block",
    },
    inputDetailsTopic: {
      margin: "4px 0",
    },
    inputDetailsTopicName: {
      color: grayColor[3],
    },
    inputDetailsTopicContent: {
      // color: grayColor[0],
    },
    inputDetailsTopicFunctionIcon: {
      cursor: "pointer",
      "& > svg": {
        width: "24px",
      },
    },
    inputDetailsSaveButton: {
      width: "90px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "6px 0",
      height: "24px",
      cursor: "pointer",
      backgroundColor: theme.palette.custom.clientColor,
      color: theme.palette.custom.clientTextColor || whiteColor,
      padding: "0 4px",
      borderRadius: "4px",
      "&.saving": {
        opacity: "0.5",
        pointerEvents: "none",
      },
    },
    inputDrawerTag: {
      display: "inline-block",
      fontSize: "12px",
      padding: "0 4px",
      backgroundColor: grayColor[19],
      borderRadius: "4px",
      marginRight: "4px",
      marginBottom: "4px",
      transition: "all 0.2s",
      "&.muted": {
        fontStyle: "italic",
        color: grayColor[3],
      },
      "&.ready": {
        backgroundColor: blendColors(successColor[0], grayColor[19], 0.15),
      },
      "&:not(.no-hover)": {
        cursor: "pointer",
        "&:hover": {
          boxShadow: "0 0 5px 0 rgba(" + hexToRgb(blackColor) + ", 0.25)",
        },
      },
    },
    inputStatusWrapper: {
      margin: "6px 2px 2px",
      // padding: "0 6px",
      width: "100%",
      // border: "1px solid #f1f1f1",
      borderRadius: "4px",
      display: "flex",
      textAlign: "center",
    },
    inputStatusItem: {
      display: "flex",
      flex: "1",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 0 !important",
      backgroundColor: grayColor[3],
      color: whiteColor,
      borderRadius: "4px",
      "&.not-next": {
        backgroundColor: grayColor[4],
      },
      "&.success": {
        backgroundColor: successColor[3],
      },
      "&.warning": {
        backgroundColor: warningColor[3],
      },
      "&.error": {
        backgroundColor: dangerColor[3],
      },
      "&.running": {
        backgroundColor: grayColor[1],
        backgroundImage:
          "linear-gradient(-45deg, rgba(255, 255, 255, 0.25) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.25) 50%, rgba(255, 255, 255, 0.25) 75%, transparent 75%, transparent)",
        backgroundSize: "15px 15px",
        animation: "$move 2s linear infinite",
        color: whiteColor,
      },
    },
    inputStatusIcon: {
      marginTop: "6px",
      color: grayColor[0],
      "&.waiting": {
        color: grayColor[4],
      },
    },
    pipelineInProgressBar: {
      fontSize: "11px",
      fontStyle: "italic",
      textAlign: "center",
      width: "100%",
      backgroundColor: warningColor[0] + "88",
      backgroundImage:
        "linear-gradient(-45deg, rgba(255, 255, 255, 0.25) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.25) 50%, rgba(255, 255, 255, 0.25) 75%, transparent 75%, transparent)",
      backgroundSize: "15px 15px",
      animation: "$move 2s linear infinite",
    },
    displayNotesTable: {
      width: "100%",
      fontSize: "12px",
    },
    displayNotesTableHeader: {
      color: whiteColor,
      backgroundColor: grayColor[7],
      paddingTop: "6px",
      paddingBottom: "6px",
    },
    displayNotesTableRow: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      lineHeight: "1.25",
      minHeight: "28px",
      "&:not($displayNotesTableHeader)": {
        borderBottom: "1px solid " + grayColor[11],
        padding: "12px 0",
      },
      "& > div": {
        position: "relative",
      },
    },
    displayNotesTableColCheckStage: {
      width: "8%",
      textAlign: "center",
    },
    displayNotesTableColSeverity: {
      width: "9%",
      textAlign: "center",
      "&.severity1": {
        color: infoColor[0],
      },
      "&.severity2": {
        color: warningColor[0],
      },
      "&.severity3": {
        color: dangerColor[0],
      },
    },
    displayNotesTableColDescription: {
      width: "10%",
      textAlign: "center",
    },
    displayNotesTableColComments: {
      width: "57%",
      textAlign: "left",
    },
    displayNotesTableColCopyValues: {
      width: "7%",
      textAlign: "center",
    },
    displayNotesTableColMarkAsAnalyzed: {
      width: "9%",
      textAlign: "center",
    },
    displayNotesTableIcon: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "16px",
      height: "16px",
      cursor: "pointer",
      color: grayColor[1],
      transition: "all 0.2s",
      "&:hover": {
        color: grayColor[6],
      },
    },
    displayNotesTableMarkAsAnalyzedIcon: {
      width: "22px",
      height: "22px",
    },
    displayNotesTableMarkAsAnalyzedDoneIcon: {
      color: successColor[0],
      cursor: "help",
      "&:hover": {
        color: successColor[0],
      },
    },
    displayNotesTableSpinner: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      cursor: "progress",
    },
    customProgressBarRoot: {
      borderRadius: "3px",
    },
    customProgressBarBar: {
      height: "6px !important",
    },
    // Queue drawer styles
    queueDrawerContent: {
      padding: "0 12px",
      height: "calc(98vh - 72px)",
      overflowY: "auto",
    },
    queueButtonsRow: {
      display: "flex",
      alignItems: "center",
      margin: "8px 0",
      gap: 8,
      flexWrap: "wrap",
      rowGap: 8,
    },
    queueCheckboxWrapper: {
      display: "flex",
      alignItems: "center",
      marginLeft: "auto",
      cursor: "pointer",
    },
    queueCheckboxIcon: {
      color: grayColor[0],
    },
    queueCheckboxLabel: {
      marginLeft: 4,
      fontSize: 13,
      color: grayColor[0],
    },
    queueManualAddRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      margin: "18px 0 12px",
      flexWrap: "wrap",
      rowGap: 8,
      width: "100%",
    },
    queueSelectWrapper: {
      flex: "1 1 320px",
      minWidth: 240,
      maxWidth: "100%",
    },
    queueTableCell: {
      maxWidth: 260,
      overflow: "hidden",
      textOverflow: "ellipsis",
      paddingTop: 0,
      paddingBottom: 0,
    },
    queueTableCellActions: {
      paddingTop: 5,
      paddingBottom: 5,
      verticalAlign: "middle",
    },
    queueActionsContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
    },
    queueActionsButtons: {
      display: "inline-flex",
    },
    queueProgressBarWrapper: {
      width: "100%",
    },
    queueEmptyMessage: {
      color: grayColor[0],
      fontSize: 12,
    },
    // ``monthlyStatus``: chassi da barra do topo usado pelo InputsV2
    // (mes + refresh + fila + reset). E' compartilhado com outros
    // componentes V2 (``MonthSelectorV2``, ``useSpecialOptionsModal``)
    // que herdam o mesmo container.
    //
    // Onda 2026-05-18: a classe foi recuperada ao formato V2 original
    // (fundo branco + borda + padding + border-radius nas pontas
    // inferiores) apos o port da Variante D para o V3 — que tinha
    // esvaziado essa classe para o ``InputsV3GlobalTopBar`` cuidar
    // do proprio chassi. O efeito colateral era o painel do V2
    // "perder" o fundo branco. A V3 agora usa ``monthlyStatusV3``
    // abaixo (so' posicionamento, sem chassi — o componente
    // dedicado e' que pinta a barra).
    monthlyStatus: {
      [theme.breakpoints.up("md")]: {
        position: "fixed",
        top: "0",
        left: "50%",
        transform: "translate(-50%,0)",
        zIndex: "1200",
        minWidth: "600px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: whiteColor,
        padding: "4px 24px 2px",
        borderRadius: "0 0 8px 8px",
        borderStyle: "solid",
        borderWidth: "0 1px 1px 1px",
        borderColor: grayColor[8],
        whiteSpace: "nowrap",
      },
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "18px 0 4px",
      },
    },
    // ``monthlyStatusV3``: variante "sem chassi" usada pelo InputsV3.
    // O chassi (fundo, borda, border-radius, padding) e' renderizado
    // dentro do componente ``InputsV3GlobalTopBar`` (Variante D do
    // sandbox, mai/2026). Aqui sobra so o ENVELOPE de posicionamento
    // — manter ``align-items: stretch`` para a barra de 800x44 ocupar
    // a altura definida internamente sem ser comprimida.
    monthlyStatusV3: {
      [theme.breakpoints.up("md")]: {
        position: "fixed",
        top: "0",
        left: "50%",
        transform: "translate(-50%,0)",
        zIndex: "1200",
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        whiteSpace: "nowrap",
      },
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "18px 0 4px",
      },
    },
    downloadInProgress: {
      backgroundColor: blendColors(
        whiteColor,
        theme.palette.custom.clientColor,
        0.25,
      ),
      backgroundImage:
        "linear-gradient(-45deg, rgba(255, 255, 255, 0.25) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.25) 50%, rgba(255, 255, 255, 0.25) 75%, transparent 75%, transparent)",
      backgroundSize: "15px 15px",
      animation: "$move 3s linear infinite",
      color: whiteColor,
      pointerEvents: "none",
    },
    inlineButton: {
      color: grayColor[0],
      margin: "0 2px",
      padding: "0",
      height: "24px",
      width: "36px",
      borderRadius: "4px",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      "&.disabled": {
        color: blendColors(grayColor[0], whiteColor, 0.4),
        cursor: "auto",
      },
      "&:hover:not(.disabled)": {
        backgroundColor: blendColors(grayColor[0], whiteColor, 0.2),
      },
    },
    uploadProgressBar: {
      width: "100%",
      overflow: "hidden",
      textAlign: "center",
      transition: "all 0.2s",
      height: "0",
      "&.show": {
        height: "40px",
      },
    },
    attention: {
      animation: "$attention 3s ease infinite",
      animationDelay: "0.5s",
      transformOrigin: "top center",
    },
    "@keyframes attention": {
      "10%": {
        transform: "scale(1.1) translate(0,-50%)",
      },
      "13.33%,20%": {
        transform: "rotate(-10deg) scale(1.1) translate(0,-50%)",
      },
      "16.67%": {
        transform: "rotate(10deg) scale(1.1) translate(0,-50%)",
      },
      "23.33%": {
        transform: "rotate(0deg) scale(1.1) translate(0,-50%)",
      },
      "33%": {
        transform: "scale(1) translate(0,-50%)",
      },
    },
    largeTooltip: {
      fontSize: "12px",
    },
    inputsLegendTooltip: {
      background: "transparent",
      padding: 0,
      maxWidth: "none",
      boxShadow: "none",
    },
    inputsLegendTooltipArrow: {
      color: "#ffffff",
    },
    viewButtonsContainer: {
      position: "absolute",
      right: "0",
      top: "50%",
      transform: "translate(0,-50%)",
      display: "flex",
    },
    viewButton: {
      margin: "0 !important",
      position: "relative",
      boxShadow: "0 0 2px rgba(0, 0, 0, 0.3) !important",
      padding: "3px 8px !important",
      "&:not($buttonSelected)": {
        backgroundColor: whiteColor + " !important",
      },
    },
    firstButton: {
      borderTopRightRadius: "0 !important",
      borderBottomRightRadius: "0 !important",
      float: "left",
    },
    middleButton: {
      borderRadius: "0 !important",
    },
    lastButton: {
      borderTopLeftRadius: "0 !important",
      borderBottomLeftRadius: "0 !important",
    },
    buttonSelected: {
      pointerEvents: "none",
    },
    generalViewLegend: {
      position: "absolute",
      top: "18px",
      left: "50%",
      transform: "translate(-50%,0)",
      fontSize: "12px",
      lineHeight: "1.25",
      fontWeight: "300",
      color: grayColor[6],
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    generalViewTable: {
      // maxHeight: "80vh",
      "& *": {
        fontSize: "14px",
        lineHeight: "1.25",
        paddingLeft: "5px",
        paddingRight: "5px",
      },
    },
    generalViewTableHeader: {
      "& *": {
        fontWeight: "300",
        backgroundColor: grayColor[13],
      },
    },
    generalViewTableBody: {
      "& *": {
        fontWeight: "300",
        color: grayColor[6],
        overflow: "hidden",
        whiteSpace: "nowrap",
      },
    },
    generalStatusTag: {
      width: "40px",
      height: "16px",
      borderRadius: "8px",
      display: "inline-block",
      position: "relative",
      "&.waiting": {
        backgroundColor: grayColor[3],
      },
      "&.success1": {
        backgroundColor: successColor[1] + "88",
      },
      "&.success2": {
        backgroundColor: successColor[4],
      },
      "&.warning": {
        backgroundColor: warningColor[1],
      },
      "&.error": {
        backgroundColor: dangerColor[1],
      },
    },
    generalStatusTagIcon: {
      position: "absolute",
      left: "1px",
      top: "1px",
      height: "14px",
      width: "14px",
      color: whiteColor,
    },
    selectFormControl: {
      paddingTop: "7px",
      margin: "5px 0 7px 0 !important",
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: grayColor[4] + "!important",
        },
        "&:after": {
          borderBottomColor: theme.palette.custom.clientColor + "!important",
        },
      },
    },
    batchWarningSnackbar: {
      borderRadius: "6px !important",
      boxShadow: "none !important",
    },
  };
};
